import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, ChevronDown, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { productService } from '../services/api';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [thumbnailPage, setThumbnailPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragStart, setDragStart] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [selectedVariantAttributes, setSelectedVariantAttributes] = useState({});
  const { addToCart } = useCart();
  const UPLOADS_URL = import.meta.env.VITE_UPLOADS_URL;

  // Mínimo de distancia para considerar un swipe
  const minSwipeDistance = 50;

  // Cantidad de miniaturas visibles por página
  const THUMBNAILS_PER_PAGE = 4;

  useEffect(() => {
    loadProduct();
  }, [id]);

  // Handlers para swipe en móvil con arrastre visual
  const onTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.targetTouches[0].clientX);
    setDragOffset(0);
  };

  const onTouchMove = (e) => {
    if (!isDragging) return;
    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - dragStart;
    setDragOffset(diff);
  };

  const onTouchEnd = (imagesLength) => {
    if (!isDragging) return;
    setIsDragging(false);

    const isLeftSwipe = dragOffset < -minSwipeDistance;
    const isRightSwipe = dragOffset > minSwipeDistance;

    if (isLeftSwipe && selectedImage < imagesLength - 1) {
      // Swipe izquierda = siguiente imagen
      const newIndex = selectedImage + 1;
      setSelectedImage(newIndex);
      const newPage = Math.floor(newIndex / THUMBNAILS_PER_PAGE);
      if (newPage !== thumbnailPage) setThumbnailPage(newPage);
    } else if (isRightSwipe && selectedImage > 0) {
      // Swipe derecha = imagen anterior
      const newIndex = selectedImage - 1;
      setSelectedImage(newIndex);
      const newPage = Math.floor(newIndex / THUMBNAILS_PER_PAGE);
      if (newPage !== thumbnailPage) setThumbnailPage(newPage);
    }

    // Resetear offset con animación
    setDragOffset(0);
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await productService.getById(id);
      setProduct(response.data);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product.stock === 0 || product.stock < quantity) {
      toast.error('No hay suficiente stock disponible');
      return;
    }
    addToCart(product, quantity);
    toast.success('Producto agregado al carrito');
  };

  const handleBuyNow = () => {
    if (product.stock === 0 || product.stock < quantity) {
      toast.error('No hay suficiente stock disponible');
      return;
    }
    addToCart(product, quantity);
    navigate('/carrito');
  };

  const handleWhatsAppConsult = () => {
    let message = `Hola! Me gustaría consultar sobre el producto: ${product.name}`;
    const variantInfo = Object.entries(selectedVariantAttributes)
      .map(([name, val]) => `${name}: ${val.value}`)
      .join(', ');
    if (variantInfo) message += ` (${variantInfo})`;
    const whatsappUrl = `https://wa.me/5492664726581?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Funciones del modal de imagen
  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextModalImage = (imagesLength) => {
    setModalImageIndex((prev) => (prev + 1) % imagesLength);
  };

  const prevModalImage = (imagesLength) => {
    setModalImageIndex((prev) => (prev - 1 + imagesLength) % imagesLength);
  };

  const formatPrice = (price) => {
    const number = Math.round(Number(price) || 0);
    return '$' + number.toLocaleString('de-DE');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2d3c5d] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando producto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Producto no encontrado</h2>
          <Link to="/productos" className="text-[#2d3c5d] hover:underline">
            Volver a productos
          </Link>
        </div>
      </div>
    );
  }

  const originalPrice = parseFloat(product.price) || 0;
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : 0;
  const hasDiscount = salePrice > 0 && salePrice < originalPrice;
  const discountedPrice = hasDiscount ? salePrice : originalPrice;

  // Buscar variante seleccionada para ajustar precio
  const selectedVariant = product.variants?.find(v =>
    v.attributes?.every(attr =>
      selectedVariantAttributes[attr.attribute_name]?.value_id === attr.attribute_value_id
    ) && v.attributes?.length === Object.keys(selectedVariantAttributes).length
  );
  const variantAdjustment = selectedVariant ? parseFloat(selectedVariant.price_adjustment) : 0;
  const finalPrice = discountedPrice + variantAdjustment;

  // Construir array de imágenes con URLs completas
  const getImageUrl = (imgPath) => {
    if (!imgPath) return '/placeholder-product.jpg';
    if (imgPath.startsWith('http')) return imgPath;
    if (imgPath.startsWith('/ecommerce-api')) {
      return `https://decohomesinrival.com.ar${imgPath}`;
    }
    return `${UPLOADS_URL}${imgPath}`;
  };

  const images = product.images && product.images.length > 0
    ? product.images.map(img => ({
        ...img,
        url: getImageUrl(img.image_path)
      }))
    : product.primary_image_url
      ? [{ url: getImageUrl(product.primary_image_url) }]
      : product.primary_image
        ? [{ url: getImageUrl(product.primary_image) }]
        : [{ url: '/placeholder-product.jpg' }];

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm text-gray-600">
          <Link to="/" className="hover:text-[#2d3c5d]">Inicio</Link>
          <span className="mx-2">/</span>
          <Link to="/productos" className="hover:text-[#2d3c5d]">Productos</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">{product.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Galería de imágenes */}
          <div className="space-y-4">
            {/* Imagen principal con navegación y swipe con arrastre */}
            <div
              className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={() => onTouchEnd(images.length)}
            >
              {/* Contenedor de todas las imágenes para el carrusel */}
              <div
                className="absolute inset-0 flex"
                style={{
                  width: `${images.length * 100}%`,
                  transform: `translateX(calc(-${(selectedImage * 100) / images.length}% + ${dragOffset}px))`,
                  transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                }}
              >
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="h-full"
                    style={{ width: `${100 / images.length}%` }}
                  >
                    {image.url && image.url !== '/placeholder-product.jpg' ? (
                      <img
                        src={image.url}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover pointer-events-none select-none"
                        draggable={false}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <svg className="w-24 h-24 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-400 font-medium">Sin imagen disponible</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Flechas de navegación en imagen principal */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => {
                      const newIndex = selectedImage === 0 ? images.length - 1 : selectedImage - 1;
                      setSelectedImage(newIndex);
                      // Actualizar página de miniaturas si es necesario
                      const newPage = Math.floor(newIndex / THUMBNAILS_PER_PAGE);
                      if (newPage !== thumbnailPage) setThumbnailPage(newPage);
                    }}
                    className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                  </button>
                  <button
                    onClick={() => {
                      const newIndex = selectedImage === images.length - 1 ? 0 : selectedImage + 1;
                      setSelectedImage(newIndex);
                      // Actualizar página de miniaturas si es necesario
                      const newPage = Math.floor(newIndex / THUMBNAILS_PER_PAGE);
                      if (newPage !== thumbnailPage) setThumbnailPage(newPage);
                    }}
                    className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
                  >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                  </button>

                  {/* Contador de imágenes */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 sm:bottom-3 sm:left-auto sm:translate-x-0 sm:right-3 bg-black/60 text-white text-xs sm:text-sm px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">
                    {selectedImage + 1} / {images.length}
                  </div>
                </>
              )}

              {/* Botón de zoom */}
              <button
                onClick={() => openImageModal(selectedImage)}
                className="absolute top-2 left-2 sm:top-3 sm:right-3 sm:left-auto w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg sm:opacity-0 sm:group-hover:opacity-100 transition-opacity cursor-pointer z-10"
              >
                <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>

              {/* Overlay clickeable para abrir modal */}
              <div
                onClick={() => openImageModal(selectedImage)}
                className="absolute inset-0 cursor-zoom-in"
                style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
              />
            </div>

            {/* Miniaturas con paginación */}
            {images.length > 1 && (
              <div className="space-y-2">
                {/* Contenedor de miniaturas */}
                <div className="flex items-center gap-2">
                  {/* Botón anterior */}
                  {images.length > THUMBNAILS_PER_PAGE && (
                    <button
                      onClick={() => setThumbnailPage(Math.max(0, thumbnailPage - 1))}
                      disabled={thumbnailPage === 0}
                      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition ${
                        thumbnailPage === 0
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}

                  {/* Grid de miniaturas */}
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    {images
                      .slice(
                        thumbnailPage * THUMBNAILS_PER_PAGE,
                        (thumbnailPage + 1) * THUMBNAILS_PER_PAGE
                      )
                      .map((image, index) => {
                        const realIndex = thumbnailPage * THUMBNAILS_PER_PAGE + index;
                        return (
                          <button
                            key={realIndex}
                            onClick={() => setSelectedImage(realIndex)}
                            className={`aspect-square rounded-lg overflow-hidden border-2 transition ${
                              selectedImage === realIndex
                                ? 'border-[#2d3c5d] ring-2 ring-[#2d3c5d]/30'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <img
                              src={image.url}
                              alt={`${product.name} ${realIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        );
                      })}
                  </div>

                  {/* Botón siguiente */}
                  {images.length > THUMBNAILS_PER_PAGE && (
                    <button
                      onClick={() =>
                        setThumbnailPage(
                          Math.min(
                            Math.ceil(images.length / THUMBNAILS_PER_PAGE) - 1,
                            thumbnailPage + 1
                          )
                        )
                      }
                      disabled={
                        thumbnailPage >= Math.ceil(images.length / THUMBNAILS_PER_PAGE) - 1
                      }
                      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition ${
                        thumbnailPage >= Math.ceil(images.length / THUMBNAILS_PER_PAGE) - 1
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Indicador de cantidad de imágenes */}
                {images.length > THUMBNAILS_PER_PAGE && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <span>
                      {selectedImage + 1} de {images.length} imágenes
                    </span>
                    <span className="text-gray-300">•</span>
                    <span>
                      Página {thumbnailPage + 1} de {Math.ceil(images.length / THUMBNAILS_PER_PAGE)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Información del producto */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-[#2d3c5d] mb-2">{product.name}</h1>
              <p className="text-gray-600">{product.short_description}</p>
            </div>

            {/* Precio */}
            <div className="bg-gray-50 p-6 rounded-lg">
              {hasDiscount && (
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-[#2d3c5d] text-white px-3 py-1 rounded-full text-sm font-bold">
                    -{Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)}%
                  </span>
                  <span className="text-gray-400 line-through text-lg">
                    {formatPrice(originalPrice)}
                  </span>
                </div>
              )}

              <div className="text-4xl font-bold text-[#2d3c5d] mb-2">
                {formatPrice(finalPrice)}
              </div>
              {product.stock === 0 && (
                <div className="mt-2 inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                  Producto agotado
                </div>
              )}
            </div>

            {/* Selector de variantes */}
            {product.available_attributes && product.available_attributes.length > 0 && (
              <div className="space-y-4">
                {product.available_attributes.map(attr => (
                  <div key={attr.attribute_id}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {attr.display_name}
                      {selectedVariantAttributes[attr.attribute_name] && (
                        <span className="font-normal text-gray-500 ml-2">
                          — {selectedVariantAttributes[attr.attribute_name].value}
                        </span>
                      )}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {attr.values.map(val => {
                        const isSelected = selectedVariantAttributes[attr.attribute_name]?.value_id === val.value_id;
                        if (attr.attribute_type === 'color') {
                          return (
                            <button
                              key={val.value_id}
                              type="button"
                              onClick={() => setSelectedVariantAttributes(prev => ({
                                ...prev,
                                [attr.attribute_name]: isSelected ? undefined : { value_id: val.value_id, value: val.value }
                              }))}
                              className={`w-10 h-10 rounded-full border-2 transition-all ${
                                isSelected
                                  ? 'ring-2 ring-offset-2 ring-[#2d3c5d] border-[#2d3c5d]'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                              style={{ backgroundColor: val.color_hex || '#ccc' }}
                              title={val.value}
                            />
                          );
                        }
                        return (
                          <button
                            key={val.value_id}
                            type="button"
                            onClick={() => setSelectedVariantAttributes(prev => ({
                              ...prev,
                              [attr.attribute_name]: isSelected ? undefined : { value_id: val.value_id, value: val.value }
                            }))}
                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                              isSelected
                                ? 'bg-[#2d3c5d] text-white border-[#2d3c5d]'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {val.value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* MODO CATÁLOGO: Cantidad y botones de compra ocultos */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad {product.stock > 0 && <span className="text-sm text-gray-500">(Stock: {product.stock})</span>}
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={product.stock === 0}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.stock || 1}
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setQuantity(Math.min(Math.max(1, val), product.stock || 1));
                  }}
                  disabled={product.stock === 0}
                  className="w-20 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d3c5d] disabled:opacity-50 disabled:bg-gray-100"
                />
                <button
                  onClick={() => setQuantity(Math.min(quantity + 1, product.stock || 1))}
                  disabled={product.stock === 0 || quantity >= product.stock}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {product.stock === 0 && (
                <p className="text-red-600 text-sm mt-2 font-medium">Producto sin stock</p>
              )}
            </div> */}

            {/* MODO CATÁLOGO: Botones de compra ocultos, solo WhatsApp visible */}
            <div className="space-y-3">
              {/* <button
                onClick={handleBuyNow}
                disabled={product.stock === 0}
                className="w-full py-3 rounded-lg font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
                style={product.stock === 0 ? {} : { backgroundColor: '#2d3c5d' }}
              >
                {product.stock === 0 ? 'Sin stock' : 'Comprar ahora'}
              </button>

              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="w-full py-3 rounded-lg font-semibold transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                style={product.stock === 0 ? {} : {
                  backgroundColor: '#eddacb',
                  color: '#2d3c5d',
                }}
              >
                {product.stock === 0 ? 'Sin stock' : 'Agregar al carrito'}
              </button> */}

              <button
                onClick={handleWhatsAppConsult}
                className="w-full py-3 rounded-lg font-semibold bg-green-500 text-white hover:bg-green-600 transition flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Consultar por WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* MODO CATÁLOGO: Retiro por Depósito oculto */}
        {/* <div className="mt-8">
          <div className="bg-gradient-to-r from-[#2d3c5d] to-[#3d4c6d] p-6 rounded-xl shadow-lg">
            <div className="flex items-start gap-4 text-white">
              <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-xl mb-2">Retiro por Depósito</h3>
                <p className="text-white/90 text-sm mb-1">
                  Pedí y retirá tu compra en nuestros depósitos sin costo adicional
                </p>
                <p className="text-white/80 text-xs">
                  Disponible para retiro dentro de las 24-48hs de confirmado el pago
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Descripción */}
        {(product.description || product.features) && (
          <div className="mt-8">
            {product.description && (
              <details open className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <summary className="px-6 py-4 font-bold text-[#2d3c5d] cursor-pointer flex items-center justify-between hover:bg-gray-50">
                  <span>Descripción</span>
                  <ChevronDown className="w-5 h-5 transition-transform details-open:rotate-180" />
                </summary>
                <div className="px-6 py-4 border-t border-gray-200">
                  <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
                </div>
              </details>
            )}

            {product.features && (
              <details className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${product.description ? 'mt-4' : ''}`}>
                <summary className="px-6 py-4 font-bold text-[#2d3c5d] cursor-pointer flex items-center justify-between hover:bg-gray-50">
                  <span>Características Destacadas</span>
                  <ChevronDown className="w-5 h-5" />
                </summary>
                <div className="px-6 py-4 border-t border-gray-200">
                  <p className="text-gray-700">{product.features}</p>
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Modal de imagen ampliada */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeImageModal}
        >
          {/* Botón cerrar */}
          <button
            onClick={closeImageModal}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
          >
            <X className="w-8 h-8 text-white" />
          </button>

          {/* Contador de imágenes */}
          <div className="absolute top-4 left-4 bg-black/50 text-white text-sm px-4 py-2 rounded-full">
            {modalImageIndex + 1} / {images.length}
          </div>

          {/* Imagen */}
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[modalImageIndex]?.url}
              alt={`${product.name} ${modalImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>

          {/* Navegación */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevModalImage(images.length);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextModalImage(images.length);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}

          {/* Miniaturas en el modal */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalImageIndex(index);
                  }}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    modalImageIndex === index
                      ? 'border-white opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={`Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
